import React from 'react';
import { Button, Checkbox, Input, Radio, Select, Space } from 'antd';
import type { CampoLeido, EstadoCampo, Relacion, ValorCampo } from '../../api/feriasApi';
import { FctEstado, FctGlifoAtencion } from '../../fct/react';
import type { FctFamilia } from '../../fct/react';
import { ESTADO_LECTURA, mismoValor } from './campos';
import type { Campo } from './campos';

// Cómo se muestra cada estado de lectura: icono y etiqueta, nunca solo color (manual, regla 4).
const FAMILIA_LECTURA: Record<EstadoCampo, FctFamilia> = {
  empty: 'neutro',
  read: 'exito',
  doubtful: 'aviso',
};

/** Un valor en palabras, para mostrar el verificado antes. */
export function textoDeValor(campo: Campo, valor: ValorCampo | undefined): string {
  if (valor === undefined || valor === '' || (Array.isArray(valor) && valor.length === 0)) {
    return '(vacío)';
  }
  if (typeof valor === 'boolean') {
    return valor ? 'Sí' : 'No';
  }
  if (typeof valor === 'string') {
    return campo.opciones?.find(([v]) => v === valor)?.[1] ?? valor;
  }
  return (valor as (string | Relacion)[])
    .map((v) => (typeof v === 'string' ? campo.opciones?.find(([o]) => o === v)?.[1] ?? v : `${v.persona} (${v.tipo})`))
    .join(', ');
}

interface Props {
  campo: Campo;
  valor: ValorCampo;
  onCambio: (valor: ValorCampo) => void;
  /** Cómo lo leyó el modelo, cuando se muestra una lectura. */
  lectura?: CampoLeido;
  /** El valor verificado antes, tras una lectura nueva de un caso ya verificado (P3). */
  anterior?: ValorCampo;
}

/**
 * Un campo de la pantalla de verificación: su control, editable diga lo que diga la lectura, con el
 * estado de la lectura y, si es dudoso, la nota del modelo; y el valor verificado antes, cuando el caso
 * se volvió a leer (diseño M3 §4 de fct-ferias).
 */
export function CampoEditor({ campo, valor, onCambio, lectura, anterior }: Props) {
  const id = `campo-${campo.clave}`;
  const dudoso = lectura?.status === 'doubtful';
  const cambiaDelAnterior = anterior !== undefined && !mismoValor(anterior, valor);

  return (
    <div className={dudoso ? 'ferias-campo ferias-campo--dudoso' : 'ferias-campo'} data-campo={campo.clave}>
      <div className="ferias-campo__cabecera">
        <label htmlFor={id} className="ferias-campo__etiqueta">
          {campo.etiqueta}
        </label>
        {lectura && <FctEstado personalizado={{ etiqueta: ESTADO_LECTURA[lectura.status], familia: FAMILIA_LECTURA[lectura.status] }} />}
      </div>

      <Control id={id} campo={campo} valor={valor} onCambio={onCambio} />

      {dudoso && lectura?.note && (
        <div className="ferias-campo__nota">
          <FctGlifoAtencion nivel="atencion" /> {lectura.note}
        </div>
      )}

      {anterior !== undefined && (
        <div className="ferias-campo__anterior">
          Verificado antes: <span className={campo.mono ? 'fct-mono' : undefined}>{textoDeValor(campo, anterior)}</span>
          {cambiaDelAnterior && (
            <Button size="small" type="link" onClick={() => onCambio(anterior)}>
              Usar este valor
            </Button>
          )}
        </div>
      )}
    </div>
  );
}

// El control para la forma del campo.
function Control({ id, campo, valor, onCambio }: { id: string } & Pick<Props, 'campo' | 'valor' | 'onCambio'>) {
  switch (campo.forma) {
    case 'texto':
      return (
        <Input
          id={id}
          className={campo.mono ? 'fct-mono' : undefined}
          maxLength={campo.maxLength}
          value={valor as string}
          onChange={(e) => onCambio(e.target.value)}
        />
      );
    case 'textoLargo':
      return <Input.TextArea id={id} autoSize={{ minRows: 2 }} value={valor as string} onChange={(e) => onCambio(e.target.value)} />;
    case 'opcion':
      return (
        <Radio.Group
          id={id}
          value={valor as string}
          onChange={(e) => onCambio(e.target.value as string)}
          options={(campo.opciones ?? []).map(([value, label]) => ({ value, label }))}
        />
      );
    case 'si-no':
      return (
        <Checkbox id={id} checked={valor as boolean} onChange={(e) => onCambio(e.target.checked)}>
          Marcado
        </Checkbox>
      );
    case 'casillas':
      return (
        <div id={id}>
          <Checkbox.Group
            value={valor as string[]}
            onChange={(v) => onCambio(v as string[])}
            options={(campo.opciones ?? []).map(([value, label]) => ({ value, label }))}
          />
        </div>
      );
    case 'etiquetas':
      return (
        <Select
          id={id}
          mode="tags"
          style={{ width: '100%' }}
          placeholder="Escriba cada provincia y pulse Intro"
          value={valor as string[]}
          onChange={(v: string[]) => onCambio(v)}
          open={false}
        />
      );
    case 'relaciones':
      return <Relaciones id={id} valor={valor as Relacion[]} onCambio={onCambio} />;
  }
}

// Una lista de {persona, tipo}, una fila cada una, que se añaden y quitan a mano.
function Relaciones({ id, valor, onCambio }: { id: string; valor: Relacion[]; onCambio: (v: Relacion[]) => void }) {
  const cambiar = (indice: number, cambio: Partial<Relacion>) => onCambio(valor.map((r, i) => (i === indice ? { ...r, ...cambio } : r)));

  return (
    <div id={id}>
      {valor.map((relacion, indice) => (
        <Space key={indice} style={{ display: 'flex', marginBottom: 8 }}>
          <Input
            aria-label={`Persona o entidad ${indice + 1}`}
            placeholder="Persona o entidad"
            value={relacion.persona}
            onChange={(e) => cambiar(indice, { persona: e.target.value })}
          />
          <Input
            aria-label={`Tipo de relación ${indice + 1}`}
            placeholder="Tipo de relación"
            value={relacion.tipo}
            onChange={(e) => cambiar(indice, { tipo: e.target.value })}
          />
          <Button size="small" onClick={() => onCambio(valor.filter((_, i) => i !== indice))}>
            Quitar
          </Button>
        </Space>
      ))}
      <Button size="small" onClick={() => onCambio([...valor, { persona: '', tipo: '' }])}>
        Añadir relación
      </Button>
    </div>
  );
}
